import type { AuditEntry, OrganizationResponse } from '@/api/generated';
import { actionPhrase } from '@/components/activity/actions';
import { fieldLabel } from '@/components/activity/formatValue';
import { actorLabel } from '@/components/activity/words';
import { STATUS_LABELS } from '@/lib/organization';

import type { ActivityEntry } from './ActivityPanel';

function label(status: string) {
  return STATUS_LABELS[status] ?? status;
}

function time(value: string) {
  return new Date(value).getTime();
}

/**
 * The account's history, worked out from the dates the record carries.
 *
 * Used only for an account older than the audit log, whose feed is empty. It
 * is honest about that: these are the lifecycle moments the record itself
 * knows, with no actor on any of them, because nothing recorded who.
 */
export function toActivityEntries(record: OrganizationResponse): ActivityEntry[] {
  const entries: ActivityEntry[] = [];

  entries.push({ at: record.created_at, title: 'Account created' });

  if (record.activated_at) {
    entries.push({ at: record.activated_at, title: 'First went live' });
  }

  if (record.status_changed_at) {
    entries.push({
      at: record.status_changed_at,
      title: `Status changed to ${label(record.status)}`,
      detail: record.status_reason ?? undefined,
    });
  }

  /*
   * Only when nothing else already says it. Deactivating through the portal
   * sets both dates in the same write, so listing both would show every
   * deactivated account the same moment twice - once as a status change and
   * once again underneath it. The entry is kept for accounts deactivated
   * before the status date was recorded, which would otherwise show nothing.
   */
  if (record.deleted_at && !record.status_changed_at) {
    entries.push({ at: record.deleted_at, title: 'Deactivated' });
  }

  /*
   * Last, and only when it is genuinely later than everything else. An update
   * that was the status change would otherwise appear twice, a moment apart,
   * saying nothing the entry above it did not.
   */
  const latest = entries.reduce((newest, entry) => Math.max(newest, time(entry.at)), 0);
  if (time(record.updated_at) > latest) {
    entries.push({ at: record.updated_at, title: 'Last updated' });
  }

  return entries;
}

/**
 * The account's audit entries as the panel shows them: what was done in words,
 * by whom, and which fields it changed.
 */
export function toFeedEntries(items: AuditEntry[]): ActivityEntry[] {
  return items.map((item) => {
    const fields = (item.changes ?? []).map((change) => fieldLabel(change.field));
    return {
      id: item.id,
      at: item.occurred_at,
      title: actionPhrase(item.action),
      actor: actorLabel(item.actor),
      detail: fields.length ? `Changed: ${fields.join(', ')}` : undefined,
    };
  });
}
